import React, { useState, useEffect } from "react";
import { PencilAltIcon } from "@heroicons/react/solid";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";

import Button from "components/lib/Shared/Button";
import useFullscreenToggle from "hooks/useFullscreenToggle";
import Overlay from "components/lib/Shared/Overlay";
import PaginationControls from "../PaginationControls";
import BreadCrumbs from "../BreadCrumbs";
import {
  downloadRegisterOfTransfersAsync,
  getRegisterOfTransfersAsync,
} from "state/slices/minuteBook";
import { getEmptyRowsForRegisters, getFullName, getPoBoxAddress } from "utils";
import { DownloadIcon } from "@heroicons/react/outline";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import TableHeader from "./TableHeader";
import PopulatedRow from "./PopulatedRow";
import EmptyRow from "./EmptyRow";

function RegisterOfTransfers({ selectedTab }) {
  const { isFullscreen, ToggleFullscreenContainer, ToggleFullscreenButton } =
    useFullscreenToggle();
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPageWithData, setLastPageWithData] = useState(1);
  const [rowsPerPage] = useState(17);
  const [data, setData] = useState([]);
  const dispatch = useDispatch();
  const { company } = useSelector((state) => state.companyDashboardSlice);

  const {
    registerOfTransfers,
    getRegisterOfTransfers,
    downloadRegisterOfTransfers,
    updateAllotments,
  } = useSelector((state) => state.minuteBookSlice);

  /** Functions */
  const handleDownloadClick = () => {
    dispatch(
      downloadRegisterOfTransfersAsync({
        queryParams: { companyId: company._id, fileFormat: "xlsx" },
      })
    );
  };

  /** Effects */
  useEffect(() => {
    if (company._id) {
      dispatch(
        getRegisterOfTransfersAsync({
          queryParams: { companyId: company._id },
        })
      );
    }
  }, [company._id]);

  useEffect(() => {
    setLastPageWithData(Math.ceil(registerOfTransfers.totalCount / 17));
  }, [registerOfTransfers.items]);

  /** Returns */

  if (getRegisterOfTransfers.status === "loading") {
    return (
      <div className="col-span-6">
        <div className="flex justify-center mx-auto text-white">
          <SpinnerIcon className="text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-auto">
      <div className="flex justify-between w-full mb-4">
        <BreadCrumbs breadCrumbs={selectedTab.breadCrumbs} />
        <Button
          onClick={handleDownloadClick}
          preIcon={DownloadIcon}
          isLoading={downloadRegisterOfTransfers.status === "loading"}
          loadingText="Downloading..."
        >
          Download
        </Button>
      </div>
      <ToggleFullscreenContainer>
        <ToggleFullscreenButton />
        <div
          className={`bg-white relative ${
            isFullscreen ? "h-[88vh] overflow-auto" : ""
          }`}
        >
          {currentPage === 1 && (
            <div
              className={`overflow-auto px-1 pt-4 pb-20 space-y-2 flex flex-col text-center ${
                isFullscreen ? "px-12" : ""
              }`}
            >
              {/* REGISTER OF TRANSFERS */}
              <div>
                <h2 className="text-title6 font-medium">
                  REGISTER OF TRANSFERS
                </h2>
                <div className="flex items-center justify-center">
                  <span className="mr-2 text-sm">
                    as at {format(new Date(), "dd-MM-yyyy")}
                  </span>
                  <PencilAltIcon className="w-4 h-4" />
                </div>
                <div className="mt-4">
                  <table className="min-w-full divide-y divide-gray-300 border-separate border-spacing-1 table-auto">
                    <TableHeader />
                    <tbody className="divide-y divide-gray-200">
                      {registerOfTransfers.items.length > 0 &&
                        registerOfTransfers.items.map(
                          (
                            { transferee, transferor, transferNumber },
                            index
                          ) => (
                            <PopulatedRow
                              key={index}
                              transferee={transferee}
                              transferor={transferor}
                              transferNumber={transferNumber}
                            />
                          )
                        )}
                      {getEmptyRowsForRegisters(
                        registerOfTransfers.items.length
                      ).map((_, emptyRowIndex) => {
                        return <EmptyRow key={emptyRowIndex} />;
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          <PaginationControls
            isFullscreen={isFullscreen}
            isLastPage={currentPage === lastPageWithData}
            currentPage={currentPage}
            rowsPerPage={rowsPerPage || 0}
            totalRows={registerOfTransfers.totalCount}
            totalPages={Math.ceil(registerOfTransfers.totalCount / rowsPerPage)}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </ToggleFullscreenContainer>
    </div>
  );
}

export default RegisterOfTransfers;
